<template>
    <div v-if="dataset">
        <div
            v-if="hasNotUploadedWarning || hasNotConfiguredWarning"
            class="q-mb-md"
        >
            <QBtn
                v-if="hasNotUploadedWarning"
                color="primary"
                noCaps
                outline
                icon="cloud_upload"
                :label="$t('projects.datasets.resumeUpload')"
                class="full-width q-mb-sm"
                @click="resumeDatasetWizard('upload')"
            />

            <QBtn
                v-if="hasNotConfiguredWarning"
                color="primary"
                noCaps
                outline
                icon="vpn_key"
                :label="$t('projects.datasets.resumeConfiguration')"
                class="full-width"
                @click="resumeDatasetWizard('keys')"
            />
        </div>

        <q-expansion-item
            icon="info"
            :label="$t('projects.datasets.settings.general.generalTitle')"
            defaultOpened
            class="ContextDrawerExpansion q-mb-sm"
            headerClass="ContextDrawerExpansionHeader"
        >
            <div class="ContextDrawerExpansionContent">
                <div class="InfoRow q-mb-sm">
                    <div class="InfoRowLabel text-caption">
                        {{ $t('general.description') }}
                    </div>
                    <div class="InfoRowValue text-body2 text-primary">
                        {{ dataset.description || '-' }}
                    </div>
                </div>

                <div class="InfoRow q-mb-sm">
                    <div class="InfoRowLabel text-caption">
                        {{ $t('projects.datasets.settings.general.sourceType') }}
                    </div>
                    <div class="InfoRowValue text-body2 text-primary">
                        {{ dataset.source || '-' }}
                    </div>
                </div>

                <div class="InfoRow q-mb-sm">
                    <div class="InfoRowLabel text-caption">
                        {{ $t('general.createdAt') }}
                    </div>
                    <div class="InfoRowValue text-body2 text-primary">
                        {{ dataset.createDate ? App.dateLocationFormat(dataset.createDate, 'long') : '-' }}
                    </div>
                </div>

                <div class="InfoRow">
                    <div class="InfoRowLabel text-caption">
                        {{ $t('general.updateDate') }}
                    </div>
                    <div class="InfoRowValue text-body2 text-primary">
                        {{ dataset.tst ? App.dateLocationFormat(dataset.tst, 'long') : '-' }}
                    </div>
                </div>
            </div>
        </q-expansion-item>

        <q-expansion-item
            icon="sym_o_graph_1"
            :label="$t('projects.datasets.settings.general.metricsTitle')"
            defaultOpened
            class="ContextDrawerExpansion q-mb-sm"
            headerClass="ContextDrawerExpansionHeader"
        >
            <div class="ContextDrawerExpansionContent">
                <div
                    v-if="metricsErrorMessage"
                    class="text-negative text-caption q-mb-sm"
                >
                    {{ metricsErrorMessage }}
                </div>

                <div
                    v-if="!isLoadingMetrics && metrics"
                >
                    <div
                        v-for="key in Object.keys(metrics)"
                        :key="`metric-${key}`"
                        class="InfoRow q-mb-sm"
                    >
                        <div class="InfoRowLabel text-caption">
                            {{ $t(`projects.datasets.properties.${key}`) }}
                        </div>
                        <div class="InfoRowValue text-body2 text-primary">
                            {{ App.numberLocationFormat(metrics[key]) }}
                        </div>
                    </div>
                </div>

                <q-inner-loading :showing="isLoadingMetrics" />
            </div>
        </q-expansion-item>

        <q-expansion-item
            icon="warning"
            :label="warningsErrorsTitle"
            defaultOpened
            class="ContextDrawerExpansion ContextDrawerExpansion--last"
            headerClass="ContextDrawerExpansionHeader"
        >
            <div class="ContextDrawerExpansionContent">
                <div
                    v-if="!!(dataset.warnings || []).length"
                    class="column"
                >
                    <QBanner
                        v-for="(warning, idx) in dataset.warnings"
                        :key="`warn-${idx}`"
                        rounded
                        dense
                        class="col BannerOutline text-warning text-caption q-mb-sm"
                    >
                        <template #avatar>
                            <QIcon
                                name="warning"
                                size="16px"
                            />
                        </template>
                        {{ warning }}
                    </QBanner>
                </div>

                <QBanner
                    v-if="hasNotUploadedWarning"
                    rounded
                    dense
                    class="BannerOutline text-warning text-caption q-mb-sm"
                >
                    <template #avatar>
                        <QIcon
                            name="warning"
                            size="16px"
                        />
                    </template>
                    {{ $t('projects.datasets.notUploadedWarning') }}
                </QBanner>

                <QBanner
                    v-if="hasNotConfiguredWarning"
                    rounded
                    dense
                    class="BannerOutline text-warning text-caption q-mb-sm"
                >
                    <template #avatar>
                        <QIcon
                            name="warning"
                            size="16px"
                        />
                    </template>
                    {{ $t('projects.datasets.notConfiguredWarning') }}
                </QBanner>

                <div
                    v-if="!!(dataset.errors || []).length"
                    class="column"
                >
                    <QBanner
                        v-for="(error, idx) in dataset.errors"
                        :key="`err-${idx}`"
                        rounded
                        dense
                        class="col BannerOutline text-negative text-caption q-mb-sm"
                    >
                        <template #avatar>
                            <QIcon
                                name="error"
                                size="16px"
                            />
                        </template>
                        {{ error }}
                    </QBanner>
                </div>

                <QBanner
                    v-if="hasGenericError"
                    rounded
                    dense
                    class="BannerOutline text-negative text-caption q-mb-sm"
                >
                    <template #avatar>
                        <QIcon
                            name="error"
                            size="16px"
                        />
                    </template>
                    {{ $t('projects.datasets.generalError') }}
                </QBanner>

                <QBanner
                    v-if="!warningsCount && !errorsCount && !hasNotUploadedWarning && !hasNotConfiguredWarning && !hasGenericError"
                    rounded
                    dense
                    class="BannerOutline text-positive text-caption q-mb-sm"
                >
                    <template #avatar>
                        <QIcon
                            name="check_circle"
                            size="16px"
                        />
                    </template>
                    {{ $t('projects.datasets.settings.errors.noErrors') }}
                </QBanner>
            </div>
        </q-expansion-item>
    </div>
</template>

<script>
import VueTypes from 'vue-types'
import { Api, apiRequest } from '@/api'

export default {
    name: 'ProjectDatasetInfoDrawerPanel',
    inject: ['App'],
    props: {
        dataset: VueTypes.any,
    },
    data() {
        return {
            metrics: null,
            metricsErrorMessage: null,
            isLoadingMetrics: false,

            hasConfigurations: null,
            isLoadingConfigurations: false,
        }
    },
    computed: {
        hasNotUploadedWarning() {
            const stateId = this.dataset?.state?.identifier
            const uploaded = this.dataset?.uploaded
            return !uploaded && !stateId
        },
        hasGenericError() {
            const hasDetailedErrors = !!(this.dataset?.errors || []).length
            if (hasDetailedErrors) return false

            const state = this.dataset?.state?.identifier
            return state === 'ERROR'
        },
        hasNotConfiguredWarning() {
            if (!this.dataset?.uploaded) return false
            if (this.hasNotUploadedWarning) return false
            if (this.hasGenericError) return false

            // New backend semantics: before configuration the dataset has no state.
            const stateId = this.dataset?.state?.identifier
            return !stateId
        },
        errorsCount() {
            return (this.dataset?.errors || []).length
        },
        effectiveErrorsCount() {
            return this.errorsCount + (this.hasGenericError ? 1 : 0)
        },
        warningsCount() {
            return (this.dataset?.warnings || []).length
        },
        effectiveWarningsCount() {
            return this.warningsCount
                + (this.hasNotUploadedWarning ? 1 : 0)
                + (this.hasNotConfiguredWarning ? 1 : 0)
        },
        warningsErrorsTitle() {
            const base = this.$t('projects.datasets.settings.errors.title')
            return `${base} (${this.effectiveWarningsCount}/${this.effectiveErrorsCount})`
        },
    },
    watch: {
        dataset: {
            handler() {
                this.fetchMetrics()
                this.fetchConfigurations()
            },
            immediate: true,
        },
    },
    methods: {
        numFormatter(num) {
            if (num > 9999 && num < 1000000) {
                return `${(num / 1000).toFixed(1)}K`
            }
            if (num >= 1000000) {
                return `${(num / 1000000).toFixed(1)}M`
            }
            return num
        },
        fetchMetrics() {
            if (!this.dataset?.uuid) return
            this.isLoadingMetrics = true
            this.metricsErrorMessage = null

            apiRequest(Api().datasets.getInfo({ datasetId: this.dataset.uuid }))
                .then((data) => {
                    const info = {}
                    Object.keys(data).forEach((key) => {
                        info[key] = this.numFormatter(data[key])
                    })
                    this.metrics = info
                })
                .catch(() => {
                    this.metrics = null
                    this.metricsErrorMessage = this.$t('projects.datasets.errorMessage')
                })
                .finally(() => {
                    this.isLoadingMetrics = false
                })
        },
        fetchConfigurations() {
            if (!this.dataset?.uuid) return

            this.isLoadingConfigurations = true
            this.hasConfigurations = null

            apiRequest(Api().datasets.getConfigurations({ datasetId: this.dataset.uuid }))
                .then((data) => {
                    const configs = Array.isArray(data) ? data : (data?.data || [])
                    this.hasConfigurations = (configs || []).length > 0
                })
                .catch(() => {
                    // If we can't determine configurations, don't show a misleading warning.
                    this.hasConfigurations = null
                })
                .finally(() => {
                    this.isLoadingConfigurations = false
                })
        },
        resumeDatasetWizard(step) {
            const { projectId } = this.$route.params || {}
            const datasetId = this.dataset?.uuid
            const datasetName = this.dataset?.name
            if (!projectId || !datasetId) return

            this.$router.push({
                name: 'WizardDataset',
                params: { projectId },
                query: { datasetId, step, datasetName },
            })
        },
    },
}
</script>

<style scoped lang="scss">
:deep(.ContextDrawerExpansion) {
    border-top: 1px solid $gray-04;
}

:deep(.ContextDrawerExpansion.ContextDrawerExpansion--last) {
    border-bottom: 1px solid $gray-04;
}

:deep(.ContextDrawerExpansionHeader .q-item__label) {
    font-weight: 700;
}

:deep(.ContextDrawerExpansionHeader .q-item__section--avatar .q-icon) {
    color: $primary;
}

.ContextDrawerExpansionContent {
    padding: 8px 16px 16px;
}

.InfoRow {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
    flex-wrap: wrap;
}

.InfoRowLabel {
    flex: 0 1 auto;
}

.InfoRowValue {
    flex: 1 1 auto;
    min-width: 0;
    text-align: right;
    word-break: break-word;
}

.BannerOutline {
    background: transparent;
    border: 1px solid currentColor;
}

</style>
